import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import gql from "graphql-tag";
import * as React from "react";

import Link from "@khanacademy/wonder-blocks-link";
import Pill from "@khanacademy/wonder-blocks-pill";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useQuery} from "@khan/data-gql";
import {gqlOp} from "@khan/data-gql-core";
import {
    KHANMIGO_TEACHER_TOOLS_SUPPORTED_LOCALES,
    KHANMIGO_TEACHER_TOOLS_UNSUPPORTED_LOCALES,
    useIsCountryBlocked,
} from "@khan/khanmigo-access";
import {RequestInfo} from "@khan/shared-core";
import {preserveMarketingParams, useLocation} from "@khan/urls";

import {isDateOver18} from "#shared-package/is-over-18.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import type {userBirthMonthYear} from "./__graphql-types__/userBirthMonthYear.ts";
import {KhanmigoActivitiesDropdown} from "./khanmigo-activities-dropdown.tsx";
import responsiveStyles from "./responsive-nav-menu/styles.ts";

export const BIRTH_MONTH_YEAR_QUERY = gqlOp<userBirthMonthYear>(gql`
    query userBirthMonthYear {
        user {
            id
            birthMonthYear
        }
    }
`);

type Props = {
    profile: ProfileModelType | null;
    profileLoading?: boolean;
    navItemStyles: Array<CSSProperties | null | undefined>;
    isResponsiveNavMenu: boolean;
    isOpen: boolean;
    onToggle: (opened: boolean) => void;
};

// This component can render one of three header links depending on multiple criteria:
// Show "Khanmigo” to any users who have Khanmigo but are NOT teachers
//  links to /profile/me/khanmigo/activities
// Show “AI for Teachers [FREE]” to any users who do not have Khanmigo and are
//  at least 18 (or unknown birthday) > links to https://khanmigo.ai/teachers
// Show “AI for Teachers” to any users who are teachers with Khanmigo enabled
//  links to  /teacher/khanmigo-tools
// TODO alexjuy: We probably want all of the rendered bits here to be separate
// components. This component should be solely for the wham link, and the logic
// to determine which one is shown should be in the parent (perhaps in the new ai-nav-item component?)
const WhamLink = ({
    profile,
    profileLoading,
    navItemStyles,
    isResponsiveNavMenu,
    isOpen,
    onToggle,
}: Props): React.ReactElement | null => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const location = useLocation();
    const [userBirthMonthYear] = useQuery(BIRTH_MONTH_YEAR_QUERY);
    const {remoteCountry, kaLocale} = RequestInfo;
    const isRemoteCountryBlocked = useIsCountryBlocked(remoteCountry);
    const whamEligibleRestOfWorld = useFlagOrCookie(
        "wham-eligible-rest-of-world-fe",
    );

    if (userBirthMonthYear.status === "loading") {
        return null;
    }

    // Don't show while profile is loading
    if (profileLoading) {
        return null;
    }

    const whamHref = preserveMarketingParams(
        "https://khanmigo.ai/teachers",
        new URLSearchParams(location.search),
    );

    // TODO alexjuy: Find out if we still want this event
    // const activitiesAnalyticsEvent = () =>
    //     publishAnalyticsEvent({
    //         eventType: "SiteHeaderAiGuideActivitiesLinkClicked",
    //         eventSchemaVersion: 2,
    //     });

    const whamAnalyticsEvent = () => {
        publishAnalyticsEvent({
            eventType: "AiGuideSiteHeaderLinkClicked",
            eventSchemaVersion: 1,
            currentPageURL: location.href,
        });
    };

    // For users with Khanmigo access, we link to AI Activities
    if (profile?.get("isAIGuideEnabled")) {
        return isResponsiveNavMenu ? null : (
            <KhanmigoActivitiesDropdown
                profile={profile}
                navItemStyles={navItemStyles}
                isOpen={isOpen}
                onToggle={onToggle}
            />
        );
    }

    // We don't want to advertise Khanmigo to unsupported. When the flag is
    // enabled, we check the unsupported locales list. When the flag is
    // disabled, we check the supported locales list.
    if (
        (whamEligibleRestOfWorld &&
            KHANMIGO_TEACHER_TOOLS_UNSUPPORTED_LOCALES[
                kaLocale.toLowerCase()
            ]) ||
        (!whamEligibleRestOfWorld &&
            !KHANMIGO_TEACHER_TOOLS_SUPPORTED_LOCALES[kaLocale.toLowerCase()])
    ) {
        return null;
    }

    // Use the shared Khanmigo access hook, which respects both the
    // WHAM rest-of-world feature flag and the list of blocked countries.
    if (isRemoteCountryBlocked) {
        return null;
    }

    // MAP Accelerator-*only* districts currently do not have access to Khanmigo
    if (
        profile?.get("includesKmapDistrictOwnedData") &&
        !profile?.get("includesK4dDistrictOwnedData")
    ) {
        return null;
    }

    // Don't show wham link to users under age gate, child accounts, or self-declared under 18
    if (
        profile?.get("underAgeGate") ||
        profile?.get("isChildAccount") ||
        (userBirthMonthYear.status === "success" &&
            userBirthMonthYear.data.user?.birthMonthYear &&
            !isDateOver18(userBirthMonthYear.data.user?.birthMonthYear))
    ) {
        return null;
    }

    // For users who do not have AI guide enabled, show wham link
    if (!profile?.get("isAIGuideEnabled")) {
        // return isResponsiveNavMenu ? whamLinkResponsive : whamLink;
        return (
            <Link
                key={isResponsiveNavMenu ? "mobile-wham-link" : "wham-link"}
                target="_blank"
                testId={
                    isResponsiveNavMenu
                        ? "mobile-header-wham-link"
                        : "header-wham-link"
                }
                href={whamHref}
                style={[
                    navItemStyles,
                    !isResponsiveNavMenu && responsiveStyles.hideWhenMedium,
                    {display: "flex"},
                ]}
                onClick={whamAnalyticsEvent}
            >
                <>
                    {isResponsiveNavMenu ? (
                        <LabelLarge>{t`AI for Teachers`}</LabelLarge>
                    ) : (
                        t`AI for Teachers`
                    )}
                    <Pill size="small" kind="accent" style={styles.pill}>
                        {t`Free`}
                    </Pill>
                </>
            </Link>
        );
    }

    return null;
};

const styles = StyleSheet.create({
    pill: {
        textTransform: "uppercase",
        alignSelf: "center",
        marginInlineStart: 5,
        "@media screen and (min-width: 1024px) /* mediaQueries.lgOrLarger */": {
            display: "none",
        },
        "@media screen and (min-width: 1200px) /* mediaQueries.xl */": {
            display: "inherit",
        },
    },
});

export default WhamLink;
